import Decimal from 'decimal.js'

export const formatNumericValue = (
  value: number | string | Decimal,
  decimals?: number,
  roundUp?: boolean,
): string => {
  const numberValue = Number(value)
  let formattedValue
  if (numberValue > -0.000000000001 && numberValue < 0.00000000001) {
    formattedValue = '0'
  } else if (decimals !== undefined) {
    formattedValue = roundUp
      ? roundValue(numberValue, decimals, true)
      : roundValue(numberValue, decimals)
  } else if (Math.abs(numberValue) >= 1000) {
    formattedValue = roundUp
      ? roundValue(numberValue, 0, true)
      : roundValue(numberValue, 0)
  } else if (Math.abs(numberValue) >= 0.01) {
    formattedValue = roundUp
      ? roundValue(numberValue, 3, true)
      : roundValue(numberValue, 3)
  } else if (Math.abs(numberValue) >= 0.001) {
    formattedValue = roundUp
      ? roundValue(numberValue, 4, true)
      : roundValue(numberValue, 4)
  } else if (Math.abs(numberValue) >= 0.00001) {
    formattedValue = roundUp
      ? roundValue(numberValue, 7, true)
      : roundValue(numberValue, 7)
  } else if (Math.abs(numberValue) >= 0.0000001) {
    formattedValue = roundUp
      ? roundValue(numberValue, 9, true)
      : roundValue(numberValue, 9)
  } else {
    formattedValue = roundUp
      ? roundValue(numberValue, 11, true)
      : roundValue(numberValue, 11)
  }
  return formattedValue
}

export const formatCurrencyValue = (
  value: number | string | Decimal,
  decimals?: number,
): string => {
  const numberValue = Number(value)
  let formattedValue
  if (numberValue > -0.0000000001 && numberValue < 0.000000001) {
    formattedValue = '$0.00'
  } else if (decimals) {
    formattedValue = Intl.NumberFormat('en', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
      style: 'currency',
      currency: 'USD',
    }).format(numberValue)
  } else if (Math.abs(numberValue) >= 1000) {
    formattedValue = usdFormatter0.format(numberValue)
  } else if (Math.abs(numberValue) >= 0.1) {
    formattedValue = usdFormatter2.format(numberValue)
  } else {
    formattedValue = usdFormatter3Sig.format(numberValue)
  }

  if (formattedValue === '-$0.00') return '$0.00'
  return formattedValue
}

const roundValue = (
  value: number | string | Decimal,
  decimals: number,
  roundUp?: boolean,
): string => {
  const decimalValue = value instanceof Decimal ? value : new Decimal(value)
  const roundMode = roundUp ? Decimal.ROUND_UP : Decimal.ROUND_FLOOR
  const roundedValue = decimalValue
    .toDecimalPlaces(decimals, roundMode)
    .toNumber()
  if (decimals === 2) return digits2.format(roundedValue)
  if (decimals === 3) return digits3.format(roundedValue)
  if (decimals === 4) return digits4.format(roundedValue)
  if (decimals === 5) return digits5.format(roundedValue)
  if (decimals === 6) return digits6.format(roundedValue)
  if (decimals === 7) return digits7.format(roundedValue)
  if (decimals === 8) return digits8.format(roundedValue)
  if (decimals === 9) return digits9.format(roundedValue)
  return roundedValue.toLocaleString(undefined, {
    maximumFractionDigits: decimals,
  })
}

const digits2 = new Intl.NumberFormat('en', { maximumFractionDigits: 2 })
const digits3 = new Intl.NumberFormat('en', { maximumFractionDigits: 3 })
const digits4 = new Intl.NumberFormat('en', { maximumFractionDigits: 4 })
const digits5 = new Intl.NumberFormat('en', { maximumFractionDigits: 5 })
const digits6 = new Intl.NumberFormat('en', { maximumFractionDigits: 6 })
const digits7 = new Intl.NumberFormat('en', { maximumFractionDigits: 7 })
const digits8 = new Intl.NumberFormat('en', { maximumFractionDigits: 8 })
const digits9 = new Intl.NumberFormat('en', { maximumFractionDigits: 9 })

export const numberFormat = new Intl.NumberFormat('en', {
  maximumSignificantDigits: 7,
})

export const floorToDecimal = (
  value: number | string | Decimal,
  decimals: number,
): Decimal => {
  const decimal = value instanceof Decimal ? value : new Decimal(value)

  return decimal.toDecimalPlaces(decimals, Decimal.ROUND_FLOOR)
}

const usdFormatter0 = Intl.NumberFormat('en', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
  style: 'currency',
  currency: 'USD',
})

const usdFormatter2 = Intl.NumberFormat('en', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  style: 'currency',
  currency: 'USD',
})

const usdFormatter3Sig = Intl.NumberFormat('en', {
  minimumSignificantDigits: 3,
  maximumSignificantDigits: 3,
  style: 'currency',
  currency: 'USD',
})

export const countLeadingZeros = (x: number) => {
  if (x % 1 == 0) {
    return 0
  } else {
    return -1 - Math.floor(Math.log10(x % 1))
  }
}

export const getDecimalCount = (value: number): number => {
  if (Number.isNaN(Number(value)) || Math.floor(value) === value) {
    return 0
  }

  const valueStr = value.toString()
  if (valueStr.includes('.')) {
    return valueStr.split('.')[1].length
  }
  if (valueStr.includes('e')) {
    return parseInt(valueStr.split('e-')[1] || '0')
  }
  return 0
}

export const numberCompacter = Intl.NumberFormat('en', {
  maximumFractionDigits: 2,
  notation: 'compact',
})

export const formatNumberWithoutScientific = (num: number) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 20,
    useGrouping: false,
  }).format(num)
}

export const formatNumberWithSubscript = (number: number | string) => {
  const numStr = typeof number === 'string' ? number : number.toString()

  if (parseFloat(numStr) >= 0.0001) {
    return numStr
  }

  const decimalIndex = numStr.indexOf('.')

  if (decimalIndex === -1) return numStr

  const wholePart = numStr.slice(0, decimalIndex + 1)
  const fractionalPart = numStr.slice(decimalIndex + 1)

  let trailingZeros = 0
  let significantDigits = ''

  for (const char of fractionalPart) {
    if (char === '0' && significantDigits.length === 0) {
      trailingZeros++
    } else {
      significantDigits += char
    }
  }

  const subscriptZeros =
    trailingZeros > 0 ? String.fromCharCode(0x2080 + trailingZeros) : ''

  const formattedNumber = `${wholePart}0${subscriptZeros}${significantDigits}`

  return formattedNumber
}

export const formatExactPrice = (price: number): string => {
  const str = price.toString()

  if (str.includes('e')) {
    return new Decimal(price).toFixed()
  }

  const roundedStr = Number(price.toPrecision(15)).toString()

  return str.length > roundedStr.length ? roundedStr : str
}
